import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AccountBalanceWalletOutlined,
  ArrowForward,
  Inventory2Outlined,
  PeopleAltOutlined,
  PointOfSaleOutlined,
  ReceiptLongOutlined,
  Refresh,
  ScheduleOutlined,
  ShoppingCartOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { DashboardSummary, Sale } from '../../types';
import { errorMessage } from '../../utils/errorMessage';
import { formatCurrency } from '../../utils/currency';
import PageHeader from '../common/PageHeader';
import QuickActions from './QuickActions';

interface HomeData {
  summary: DashboardSummary;
  recentSales: Sale[];
}

interface HomeAlert {
  title: string;
  detail: string;
  color: string;
  icon: React.ReactNode;
  path: string;
}

const dateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const currentMonthRange = (now = new Date()) => ({
  startDate: dateParam(new Date(now.getFullYear(), now.getMonth(), 1)),
  endDate: dateParam(now),
});

const HomeMetric: React.FC<{
  title: string;
  value: string;
  hint: string;
  color: string;
  icon: React.ReactNode;
  loading: boolean;
}> = ({ title, value, hint, color, icon, loading }) => {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{
        p: { xs: 1.5, md: 2 },
        borderRadius: 2,
        borderColor: alpha(color, theme.palette.mode === 'dark' ? 0.4 : 0.2),
        background: `linear-gradient(135deg, ${alpha(color, theme.palette.mode === 'dark' ? 0.14 : 0.06)} 0%, ${theme.palette.background.paper} 75%)`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Box sx={{ display: 'grid', placeItems: 'center', width: 32, height: 32, borderRadius: 1.25, color, bgcolor: alpha(color, 0.12) }}>
          {icon}
        </Box>
      </Stack>
      <Typography sx={{ mt: 1.25, fontSize: { xs: 22, md: 26 }, fontWeight: 800 }}>
        {loading ? <Skeleton width={125} /> : value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {loading ? <Skeleton width={90} /> : hint}
      </Typography>
    </Card>
  );
};

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const theme = useTheme();
  const navigate = useNavigate();
  const scopeKey = `${user?.id ?? 'anonymous'}:${user?.companyId ?? 'none'}:${user?.role ?? 'none'}`;
  const range = currentMonthRange();
  const homeQuery = useQuery<HomeData>({
    queryKey: ['dashboard', 'home', scopeKey, range.startDate, range.endDate],
    queryFn: async ({ signal }) => {
      const [summary, salesPage] = await Promise.all([
        apiClient.getDashboardSummary(range.startDate, range.endDate, signal),
        apiClient.getSales({ page: 0, size: 5, sort: 'saleDate', direction: 'desc', includeItems: false, ...range }, signal),
      ]);
      return { summary, recentSales: salesPage.content };
    },
    staleTime: 30_000,
    retry: false,
    enabled: Boolean(user),
  });
  const plansSummary = useQuery({
    queryKey: ['payment-plans', 'summary', scopeKey],
    queryFn: () => apiClient.paymentPlans.summary(),
    enabled: Boolean(user && apiClient.paymentPlans),
    retry: false,
    staleTime: 30_000,
  });

  const data = homeQuery.data ?? null;
  const loading = homeQuery.isLoading;
  const error = homeQuery.error ? errorMessage(homeQuery.error, t('dashboard.homeLoadError')) : null;
  const fetchHome = () => { void homeQuery.refetch(); };

  const summary = useMemo(() => {
    const dashboard = data?.summary;
    return {
      monthSales: dashboard?.totalSales ?? 0,
      monthCollection: dashboard?.totalCollection ?? 0,
      receivables: dashboard?.totalReceivables ?? 0,
      monthSaleCount: dashboard?.periodSalesCount ?? 0,
      customersWithBalance: dashboard?.customersWithBalance ?? 0,
      openSales: dashboard?.openSales ?? 0,
      recentSales: data?.recentSales ?? [],
      zeroStockCount: dashboard?.zeroStockCount ?? 0,
      unpaidExpenseCount: dashboard?.unpaidExpenseCount ?? 0,
      unpaidExpensesAmount: dashboard?.unpaidExpensesAmount ?? 0,
      batchCount: (dashboard?.expiredBatchCount ?? 0) + (dashboard?.expiringBatchCount ?? 0),
      paymentPlanAttention: dashboard?.paymentPlanAttention,
    };
  }, [data]);

  const alerts = useMemo<HomeAlert[]>(() => {
    const items: HomeAlert[] = [];
    if (summary.zeroStockCount > 0) {
      items.push({
        title: t('dashboard.outOfStockProducts'),
        detail: t('dashboard.outOfStockProductsDetail').replace('{0}', String(summary.zeroStockCount)),
        color: theme.palette.error.main,
        icon: <Inventory2Outlined fontSize="small" />,
        path: '/items',
      });
    }
    if (summary.unpaidExpenseCount > 0) {
      items.push({
        title: t('dashboard.unpaidExpenses'),
        detail: t('dashboard.unpaidExpensesDetail').replace('{0}', formatCurrency(summary.unpaidExpensesAmount)),
        color: theme.palette.warning.main,
        icon: <ReceiptLongOutlined fontSize="small" />,
        path: '/expenses',
      });
    }
    if (summary.batchCount > 0) {
      items.push({
        title: t('dashboard.expiringBatches'),
        detail: t('dashboard.expiringBatchesDetail').replace('{0}', String(summary.batchCount)),
        color: theme.palette.info.main,
        icon: <WarningAmberOutlined fontSize="small" />,
        path: '/items',
      });
    }
    const paymentPlanAttention = summary.paymentPlanAttention;
    if (paymentPlanAttention && paymentPlanAttention.overdueInstallmentCount > 0) {
      items.push({
        title: t('dashboard.overdueInstallments'),
        detail: t('dashboard.overdueInstallmentsDetail')
          .replace('{0}', String(paymentPlanAttention.overdueCustomerCount))
          .replace('{1}', String(paymentPlanAttention.overdueInstallmentCount))
          .replace('{2}', formatCurrency(paymentPlanAttention.overdueAmount)),
        color: theme.palette.error.main,
        icon: <ScheduleOutlined fontSize="small" />,
        path: '/debts',
      });
    }
    if (paymentPlanAttention && paymentPlanAttention.dueSoonInstallmentCount > 0) {
      items.push({
        title: t('dashboard.dueSoonInstallments'),
        detail: t('dashboard.dueSoonInstallmentsDetail')
          .replace('{0}', String(paymentPlanAttention.dueSoonCustomerCount))
          .replace('{1}', String(paymentPlanAttention.dueSoonInstallmentCount))
          .replace('{2}', formatCurrency(paymentPlanAttention.dueSoonAmount)),
        color: theme.palette.warning.main,
        icon: <ScheduleOutlined fontSize="small" />,
        path: '/debts?installmentFilter=soon',
      });
    }
    return items;
  }, [summary, t, theme.palette.error.main, theme.palette.info.main, theme.palette.warning.main]);

  const dataReady = Boolean(data);

  return (
    <Box sx={{ flexGrow: 1, pb: { xs: 2, md: 4 } }}>
      <PageHeader
        title={t('dashboard.homeTitle')}
        description={t('dashboard.homeHint')}
        icon={<PointOfSaleOutlined />}
        accent="dashboard"
        mobileMode="hidden"
        actions={(
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" endIcon={<ArrowForward />} onClick={() => navigate('/reports')}>
              {t('dashboard.openReports')}
            </Button>
            <IconButton aria-label={t('dashboard.refresh')} onClick={() => void fetchHome()} disabled={loading}>
              <Refresh />
            </IconButton>
          </Stack>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <QuickActions
        onAddCustomer={() => navigate('/customers?action=add')}
        onAddItem={() => navigate('/items?action=add')}
        onAddDebt={() => navigate('/debts?action=add')}
        onAddPayment={() => navigate('/debts?action=payment')}
      />

      {plansSummary.data && plansSummary.data.activePlanCount > 0 && <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h6">{language === 'tr' ? 'Taksit takibi' : 'Installment tracking'}</Typography>
          <Button size="small" onClick={() => navigate('/debts')}>{language === 'tr' ? 'Tümünü gör' : 'View all'}</Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
          <Typography>{language === 'tr' ? 'Geciken' : 'Overdue'}: <strong>{formatCurrency(plansSummary.data.overdueAmount)}</strong> ({plansSummary.data.overdueCount})</Typography>
          <Typography>{language === 'tr' ? 'Bugün' : 'Today'}: <strong>{formatCurrency(plansSummary.data.dueTodayAmount)}</strong></Typography>
          <Typography>{language === 'tr' ? '7 gün içinde' : 'Next 7 days'}: <strong>{formatCurrency(plansSummary.data.nextSevenDaysAmount)}</strong></Typography>
        </Stack>
      </Paper>}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.25 }, mb: 1.5, borderRadius: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('dashboard.monthSummary')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('dashboard.monthSummaryHint')}</Typography>
          </Box>
          <PointOfSaleOutlined color="primary" />
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
          <HomeMetric title={t('dashboard.totalSales')} value={formatCurrency(summary.monthSales)} hint={t('dashboard.salesCount').replace('{0}', String(summary.monthSaleCount))} color={theme.palette.success.main} icon={<PointOfSaleOutlined fontSize="small" />} loading={loading || !dataReady} />
          <HomeMetric title={t('dashboard.totalCollection')} value={formatCurrency(summary.monthCollection)} hint={t('dashboard.periodCollectionHint')} color={theme.palette.warning.main} icon={<AccountBalanceWalletOutlined fontSize="small" />} loading={loading || !dataReady} />
          <HomeMetric title={t('dashboard.totalReceivables')} value={formatCurrency(summary.receivables)} hint={t('dashboard.receivablesHint').replace('{0}', String(summary.customersWithBalance)).replace('{1}', String(summary.openSales))} color={theme.palette.info.main} icon={<PeopleAltOutlined fontSize="small" />} loading={loading || !dataReady} />
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 1.5 }}>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.25 }, borderRadius: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('dashboard.attention')}</Typography>
            <WarningAmberOutlined color="warning" />
          </Stack>
          {loading || !dataReady ? <Stack spacing={1}><Skeleton variant="rounded" height={56} /><Skeleton variant="rounded" height={56} /></Stack> : alerts.length === 0 ? (
            <Typography color="text.secondary" variant="body2">{t('dashboard.noAlerts')}</Typography>
          ) : (
            <Stack spacing={0.75}>
              {alerts.map(alert => (
                <Button key={alert.title} onClick={() => navigate(alert.path)} sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 1, color: 'text.primary', bgcolor: alpha(alert.color, 0.07), borderRadius: 1.5, '&:hover': { bgcolor: alpha(alert.color, 0.13) } }} endIcon={<ArrowForward sx={{ color: alert.color }} />}>
                  <Box sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, mr: 1, flexShrink: 0, borderRadius: 1, color: alert.color, bgcolor: alpha(alert.color, 0.12) }}>{alert.icon}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{alert.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{alert.detail}</Typography>
                  </Box>
                </Button>
              ))}
            </Stack>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.25 }, borderRadius: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('dashboard.recentSales')}</Typography>
          <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/sales', { state: { showSalesHistory: true } })}>{t('dashboard.viewAll')}</Button>
          </Stack>
          {loading || !dataReady ? <Stack spacing={1}><Skeleton variant="rounded" height={45} /><Skeleton variant="rounded" height={45} /><Skeleton variant="rounded" height={45} /></Stack> : summary.recentSales.length === 0 ? (
            <Typography color="text.secondary" variant="body2">{t('dashboard.noRecentSales')}</Typography>
          ) : (
            <Stack spacing={0.5}>
              {summary.recentSales.map(sale => (
                <Button key={sale.id} onClick={() => navigate('/sales', { state: { showSalesHistory: true } })} sx={{ justifyContent: 'flex-start', textAlign: 'left', px: 1, py: 0.75, color: 'text.primary', borderRadius: 1.25, '&:hover': { bgcolor: 'action.hover' } }}>
                  <Box sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, mr: 1, borderRadius: 1, color: theme.palette.success.main, bgcolor: alpha(theme.palette.success.main, 0.12) }}><ShoppingCartOutlined fontSize="small" /></Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>{sale.customerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{sale.saleNumber}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 800 }}>{formatCurrency(sale.totalAmount)}</Typography>
                </Button>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default HomePage;
